import { ScrollArea } from '@ads/components-react';
import type { ProductStockLevelData } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { cn, formatNumber } from '@linka/utils';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface ProductsStockLevelProps {
  data: ProductStockLevelData[];
}

const STOCK_LOW = 25;
const STOCK_MID = 75;

function stockColorClass(usagePercentage: number): string {
  if (usagePercentage < STOCK_LOW) return 'bg-base-success-400';
  if (usagePercentage <= STOCK_MID) return 'bg-base-warning-400';
  return 'bg-base-danger-400';
}

export const ProductsStockLevel = ({ data }: ProductsStockLevelProps) => {
  const t = useTranslation();

  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.products-stock-level')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_PRODUCTS_STOCK_LEVEL' />
      </ChartCardHeader>
      {data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='relative min-h-[320px] flex-1'>
          <ScrollArea className='!absolute inset-0'>
            <div className='flex flex-col gap-mini'>
              {data.map((item, index) => (
                <div key={item.productId} className='flex flex-col gap-quark'>
                  <div className='flex items-center justify-between gap-nano'>
                    <span className='ts-paragraph-xs text-ctx-content-title truncate'>
                      {index + 1} · {item.productName}
                    </span>
                    <span className='ts-paragraph-xxs text-ctx-content-base whitespace-nowrap'>
                      {item.isUnlimited
                        ? t('unlimited')
                        : t('dashboard.products.stock-usage', {
                            sold: formatNumber(item.sold),
                            total: formatNumber(item.totalQuantity ?? 0),
                            percentage: formatNumber((item.usagePercentage ?? 0) / 100, { style: 'percent' }),
                          })}
                    </span>
                  </div>
                  {item.isUnlimited ? (
                    <div className='h-nano w-full rounded-pill bg-base-gray-200' />
                  ) : (
                    <div className='h-nano w-full rounded-pill bg-base-gray-200 overflow-hidden'>
                      <div
                        className={cn('h-full rounded-pill', stockColorClass(item.usagePercentage ?? 0))}
                        style={{ width: `${Math.min(item.usagePercentage ?? 0, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
